/**
 * urlSecrets — keep sign-in tokens out of the address bar, and out of analytics.
 *
 * The DDD Auth Hub returns users with Firebase custom tokens in the URL
 * (`#token=…&spokeToken=…`, or `?token=…` on older links). A custom token is a
 * one-hour sign-in credential. Anything that reads `window.location` copies it:
 * our own `page_location` params, Firebase Analytics' automatic page_view (it
 * keeps the query string), Performance Monitoring's page-load trace. Until
 * 2026-09 those tokens were reaching Google Analytics.
 *
 * Two defences, both needed:
 *   1. captureUrlSecrets() lifts the tokens out of the URL synchronously on
 *      first load — before any analytics code has run — keeps them in memory
 *      for the token exchange, and rewrites the address with replaceState.
 *      The app's entry calls it via src/captureUrlSecrets.js, its FIRST import.
 *   2. analyticsSafeUrl() is for any page_location an app builds itself:
 *      origin + path + non-sensitive query only, never the fragment.
 *
 * Every app carries an identical copy of this file. The canonical copy and the
 * drift check live in scripts/security/url-secrets — edit there, then copy.
 */

/** Parameters the hub puts in the URL on the way back to the app. */
export const HUB_URL_PARAMS = ['token', 'spokeToken', 'app'];

// Query/fragment parameter names never sent to analytics. Broad on purpose:
// dropping a harmless parameter from a report costs nothing, sending a
// credential cannot be undone.
const SENSITIVE_PARAM = /token|code|secret|password|passwd|signature|apikey|email|^key$|^sig$|^state$|^app$/i;

let captured = {};

const readFragment = (hash) => new URLSearchParams((hash || '').replace(/^#/, ''));

/**
 * Move the named parameters out of the URL and into memory.
 * The fragment is the preferred rail (it never reaches server logs), so a value
 * there wins over the same name in the query. Safe to call more than once.
 * `app` is only stripped alongside a token — on its own it is not ours to remove.
 *
 * @returns {object} every value captured so far, by name
 */
export function captureUrlSecrets(names = HUB_URL_PARAMS, win = globalThis.window) {
  if (!win?.location?.href) return { ...captured };

  let url;
  try {
    url = new URL(win.location.href);
  } catch {
    return { ...captured };
  }
  const fragment = readFragment(url.hash);
  const carriesToken = ['token', 'spokeToken'].some(
    (n) => fragment.has(n) || url.searchParams.has(n)
  );

  let fragmentChanged = false;
  let queryChanged = false;
  for (const name of names) {
    if (name === 'app' && !carriesToken) continue;
    if (fragment.has(name)) {
      const value = fragment.get(name);
      if (value && !captured[name]) captured[name] = value;
      fragment.delete(name);
      fragmentChanged = true;
    }
    if (url.searchParams.has(name)) {
      const value = url.searchParams.get(name);
      if (value && !captured[name]) captured[name] = value;
      url.searchParams.delete(name);
      queryChanged = true;
    }
  }

  if (fragmentChanged || queryChanged) {
    if (fragmentChanged) {
      const rest = fragment.toString();
      url.hash = rest ? `#${rest}` : '';
    }
    try {
      win.history.replaceState(win.history.state, '', url.toString());
    } catch {
      /* sandboxed frame — the values are still captured */
    }
  }
  return { ...captured };
}

/** A captured value, or null. */
export function getUrlSecret(name) {
  return captured[name] || null;
}

/** Forget captured values once they have been used (or have failed). */
export function clearUrlSecrets(names = null) {
  if (!names) {
    captured = {};
    return;
  }
  names.forEach((name) => {
    delete captured[name];
  });
}

/**
 * The URL to report to analytics: origin + path + query minus anything
 * sensitive. Never the fragment.
 */
export function analyticsSafeUrl(href = globalThis.window?.location?.href) {
  if (!href) return '';
  let url;
  try {
    url = new URL(href);
  } catch {
    return '';
  }
  [...url.searchParams.keys()].forEach((key) => {
    if (SENSITIVE_PARAM.test(key)) url.searchParams.delete(key);
  });
  const query = url.searchParams.toString();
  return `${url.origin}${url.pathname}${query ? `?${query}` : ''}`;
}
